import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { BadgeCheck, Check, X, Globe, Linkedin, Clock, RotateCcw, Send } from 'lucide-react';
import { fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

const STATUS_TABS = [
  { value: 'pending',  label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied',   label: 'Denied' },
  { value: '',         label: 'All' },
];

const PILL = {
  pending:  'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  denied:   'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
};

export default function AdminSpecialistApplicationsPage() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminNote, setAdminNote] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.adminListSpecialistApplications(tab));
    } catch { toast.error('Could not load applications'); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    const note = adminNote[row.id] || '';
    try {
      await api.adminDecideSpecialistApplication(row.id, decision, note);
      toast.success(`Application ${decision}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update application');
    }
  };

  return (
    <AdminLayout title="Specialist applications"
                 subtitle="Review candidates who applied through an invitation link."
                 actions={
                   <button data-testid="apps-refresh-btn" onClick={load}
                           className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-[12.5px] text-neutral-600 hover:bg-neutral-50">
                     <RotateCcw className="w-3.5 h-3.5" />Refresh
                   </button>
                 }>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button key={t.value || 'all'} data-testid={`apps-tab-${t.value || 'all'}`}
                  onClick={() => setTab(t.value)}
                  className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition ${
                    tab === t.value
                      ? 'bg-[#14110f] text-white'
                      : 'bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {loading ? (
          <p className="px-5 py-16 text-center text-[13px] text-neutral-400">Loading\u2026</p>
        ) : rows.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <BadgeCheck className="w-10 h-10 mx-auto text-neutral-300 mb-2" />
            <p className="text-[14px] text-neutral-500">No applications in this state.</p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {rows.map((r) => (
              <li key={r.id} data-testid={`app-row-${r.id}`} className="px-5 py-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#f5b300]/15 text-[#c89200] flex items-center justify-center font-extrabold text-[15px]">
                    {(r.first_name || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[15px] font-extrabold text-[#14110f]">{r.first_name} {r.last_name}</p>
                      <span className={`text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${PILL[r.status] || 'bg-neutral-100 text-neutral-500'}`}>{r.status}</span>
                      {r.years_experience != null && (
                        <span className="text-[11px] text-neutral-500 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />{r.years_experience}y exp
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-neutral-500 mt-0.5">
                      {r.email}{r.phone ? ` · ${r.phone}` : ''} · submitted {fmtDateTime(r.submitted_at)}
                    </p>
                    <p className="text-[13px] text-[#14110f] mt-1.5 font-semibold">
                      {r.title || 'Tax Specialist'}
                    </p>
                    {!!(r.languages || []).length && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {r.languages.map((l) => (
                          <span key={l} className="inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 rounded bg-[#faf6ec] text-[#7a5b00] font-semibold">
                            <Globe className="w-3 h-3" />{l}
                          </span>
                        ))}
                      </div>
                    )}
                    {r.linkedin_url && (
                      <a href={r.linkedin_url} target="_blank" rel="noreferrer"
                         className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-[#c89200] hover:underline">
                        <Linkedin className="w-3 h-3" />LinkedIn
                      </a>
                    )}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Bio</p>
                        <p className="text-[13px] text-neutral-700 mt-1 whitespace-pre-wrap">{r.bio}</p>
                      </div>
                      <div>
                        {r.certifications && (<>
                          <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Certifications</p>
                          <p className="text-[13px] text-neutral-700 mt-1 whitespace-pre-wrap">{r.certifications}</p>
                        </>)}
                        {r.pitch && (<>
                          <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold mt-3">Why WyseTribe?</p>
                          <p className="text-[13px] text-neutral-700 mt-1 whitespace-pre-wrap">{r.pitch}</p>
                        </>)}
                      </div>
                    </div>
                    {r.admin_note && r.status !== 'pending' && (
                      <p className="mt-3 text-[12.5px] text-neutral-500 italic">Admin note: &quot;{r.admin_note}&quot;</p>
                    )}
                    {r.status === 'pending' && (
                      <div className="mt-4 flex items-end gap-2">
                        <label className="flex-1">
                          <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">Admin note (sent in decision email)</span>
                          <input data-testid={`app-note-${r.id}`}
                                 value={adminNote[r.id] || ''}
                                 onChange={(e) => setAdminNote((s) => ({ ...s, [r.id]: e.target.value }))}
                                 placeholder="Optional"
                                 className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none" />
                        </label>
                        <button data-testid={`app-approve-${r.id}`} onClick={() => decide(r, 'approve')}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[12.5px] font-semibold">
                          <Check className="w-3.5 h-3.5" />Approve
                        </button>
                        <button data-testid={`app-deny-${r.id}`} onClick={() => decide(r, 'deny')}
                                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50 text-[12.5px] font-semibold">
                          <X className="w-3.5 h-3.5" />Deny
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
